import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { statsService, OrderStats, ProductStats } from '../services/statsService';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';

const COLORS = [
  '#FF6B6B', // Rouge vif
  '#4ECDC4', // Turquoise
  '#45B7D1', // Bleu ciel
  '#96CEB4', // Vert menthe
  '#FFEEAD', // Jaune pâle
  '#D4A5A5', // Rose poudré
  '#9B59B6', // Violet
  '#3498DB', // Bleu royal
  '#2ECC71', // Vert émeraude
  '#E67E22'  // Orange
];

const CHART_THEME = {
  grid: {
    stroke: '#E5E7EB',
    strokeDasharray: '3 3'
  },
  axis: {
    stroke: '#6B7280',
    tick: {
      fill: '#6B7280'
    }
  },
  tooltip: {
    backgroundColor: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: '0.5rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
  }
};

const StatCard = ({ title, value, trend, color = 'blue' }: { title: string; value: string | number; trend?: number; color?: string }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-gray-500 text-sm font-medium">{title}</h3>
    <div className="mt-2 flex items-baseline">
      <p className="text-2xl font-semibold text-gray-900">{value}</p>
      {trend !== undefined && (
        <span className={`ml-2 text-sm font-medium ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
        </span>
      )}
    </div>
  </div>
);

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
        <p className="font-medium text-gray-900">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value.toFixed(2)}€
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const AdminAnalytics = () => {
  const navigate = useNavigate();
  const [orderStats, setOrderStats] = useState<OrderStats | null>(null);
  const [productStats, setProductStats] = useState<ProductStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [updatingStock, setUpdatingStock] = useState<string | null>(null);
  const [stockInputValues, setStockInputValues] = useState<{[key: string]: number}>({});
  const [updateSuccess, setUpdateSuccess] = useState<string | null>(null);

  // State for sorting class details table
  const [classSortConfig, setClassSortConfig] = useState<{ key: string; direction: 'ascending' | 'descending' } | null>({ key: 'value', direction: 'descending' });

  useEffect(() => {
    if (!authService.isLoggedIn()) {
      navigate('/admin/login');
      return;
    }

    const fetchStats = async () => {
      try {
        setLoading(true);
        const [orderData, productData] = await Promise.all([
          statsService.getOrderStats(),
          statsService.getProductStats()
        ]);
        setOrderStats(orderData);
        setProductStats(productData);
        
        // Définir la date la plus récente comme date sélectionnée par défaut
        if (orderData.evolution.length > 0) {
          const sortedDates = [...orderData.evolution].sort((a, b) => 
            new Date(b.date).getTime() - new Date(a.date).getTime()
          );
          setSelectedDate(sortedDates[0].date);
        }
        
        setError(null);
      } catch (err) {
        setError('Erreur lors de la récupération des statistiques');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [navigate]);

  const handleStockInputChange = (productId: string, value: number) => {
    setStockInputValues(prev => ({
      ...prev,
      [productId]: value
    }));
  };

  const handleUpdateStock = async (productId: string) => {
    if (!stockInputValues[productId] && stockInputValues[productId] !== 0) return;
    
    try {
      setUpdatingStock(productId);
      await statsService.updateProductStock(productId, stockInputValues[productId]);
      
      // Update local state
      setProductStats(prevStats => {
        if (!prevStats) return null;
        
        return {
          ...prevStats,
          articleCounts: prevStats.articleCounts.map(article => {
            if (article.id === productId) {
              const newStock = stockInputValues[productId];
              return {
                ...article,
                stock: newStock,
                stockPercentage: newStock > 0 
                  ? Math.min(100, ((article.paidCount + article.pendingCount) / newStock) * 100)
                  : 0
              };
            }
            return article;
          })
        };
      });
      
      setUpdateSuccess(`Stock mis à jour pour ${productStats?.articleCounts.find(a => a.id === productId)?.name}`);
      setTimeout(() => setUpdateSuccess(null), 3000);
    } catch (err) {
      setError(`Erreur lors de la mise à jour du stock: ${err}`);
      setTimeout(() => setError(null), 3000);
    } finally {
      setUpdatingStock(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-8 pt-32">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 p-8 pt-32">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!orderStats || !productStats) {
    return null;
  }

  const getSortedClassData = () => {
    let sortableItems = Object.entries(orderStats.byClass)
    .map(([name, stats]) => {
      const paidOrders = stats.totalOrders - (stats.pendingOrders || 0) - (stats.cancelledOrders || 0);
      const paidAmount = stats.totalAmount * (paidOrders / stats.totalOrders);
      return {
        name,
        value: paidAmount,
          orders: paidOrders,
          averageBasket: paidOrders > 0 ? paidAmount / paidOrders : 0,
      };
    })
      .filter(item => item.orders > 0);

    if (classSortConfig !== null) {
      sortableItems.sort((a, b) => {
        // Ensure a and b have the key, provide a default if not.
        const aValue = (a as any)[classSortConfig.key] ?? (classSortConfig.key === 'name' ? '' : 0);
        const bValue = (b as any)[classSortConfig.key] ?? (classSortConfig.key === 'name' ? '' : 0);

        if (aValue < bValue) {
          return classSortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return classSortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  };

  const classData = getSortedClassData();

  const evolutionData = orderStats.evolution
    .map(item => {
      const paidOrders = item.orders;
      const paidAmount = item.amount;
      return {
        date: new Date(item.date).toLocaleDateString('fr-FR'),
        montant: paidAmount,
        commandes: paidOrders,
        panierMoyen: paidOrders > 0 ? paidAmount / paidOrders : 0,
        rawDate: item.date
      };
    });

  const hourlyEvolutionData = selectedDate && orderStats.hourlyEvolution[selectedDate] 
    ? orderStats.hourlyEvolution[selectedDate].map(item => ({
        heure: item.hour,
        montant: item.amount,
        commandes: item.orders,
        panierMoyen: item.orders > 0 ? item.amount / item.orders : 0
      }))
    : [];

  const availableDates = orderStats.evolution
    .map(item => ({
      label: new Date(item.date).toLocaleDateString('fr-FR'),
      value: item.date
    }))
    .sort((a, b) => new Date(b.value).getTime() - new Date(a.value).getTime());

  const productData = productStats.topProducts
    .map(product => {
      const paidSold = product.totalSold - (product.pendingSold || 0);
      const paidRevenue = product.totalRevenue * (paidSold / product.totalSold);
      return {
        name: product.name,
        ventes: paidSold,
        revenu: paidRevenue
      };
    })
    .filter(product => product.ventes > 0);

  // Calculer les statistiques des commandes payées
  const paidOrders = orderStats.totalOrders - orderStats.pendingOrders - orderStats.cancelledOrders;
  const paidAmount = orderStats.totalAmount;

  // Ajoutez l'élément de notification pour les mises à jour réussies
  const successNotification = updateSuccess && (
    <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg">
      {updateSuccess}
    </div>
  );

  const requestSortClassTable = (key: string) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (classSortConfig && classSortConfig.key === key && classSortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setClassSortConfig({ key, direction });
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8 pt-32">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Analyses avancées</h1>
          <button
            onClick={() => navigate('/admin')}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Retour au tableau de bord
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Commandes payées"
            value={paidOrders}
          />
          <StatCard
            title="Chiffre d'affaires"
            value={`${paidAmount.toFixed(2)}€`}
          />
          <StatCard
            title="Panier moyen"
            value={`${(paidAmount / paidOrders).toFixed(2)}€`}
          />
          <StatCard
            title="Commandes en attente"
            value={`${orderStats.pendingAmount.toFixed(2)}€`}
          />
          <StatCard
            title="Commandes distribuées"
            value={orderStats.distributedOrdersCount || 0}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-gray-500 text-sm font-medium mb-4">Évolution des ventes par jour</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={evolutionData} style={CHART_THEME}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_THEME.grid.stroke} />
                  <XAxis dataKey="date" stroke={CHART_THEME.axis.stroke} tick={CHART_THEME.axis.tick} />
                  <YAxis stroke={CHART_THEME.axis.stroke} tick={CHART_THEME.axis.tick} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="montant" 
                    stroke={COLORS[0]} 
                    strokeWidth={2}
                    name="Chiffre d'affaires"
                    dot={{ fill: COLORS[0], strokeWidth: 2 }}
                    activeDot={{ r: 8, fill: COLORS[0] }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="panierMoyen" 
                    stroke={COLORS[1]} 
                    strokeWidth={2}
                    name="Panier moyen"
                    dot={{ fill: COLORS[1], strokeWidth: 2 }}
                    activeDot={{ r: 8, fill: COLORS[1] }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-gray-500 text-sm font-medium mb-4">Répartition par classe</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={classData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {classData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={COLORS[index % COLORS.length]}
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-gray-500 text-sm font-medium mb-4">Top produits</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={productData} style={CHART_THEME}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_THEME.grid.stroke} />
                  <XAxis dataKey="name" stroke={CHART_THEME.axis.stroke} tick={CHART_THEME.axis.tick} />
                  <YAxis stroke={CHART_THEME.axis.stroke} tick={CHART_THEME.axis.tick} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar 
                    dataKey="revenu" 
                    fill={COLORS[2]} 
                    name="Revenu (€)"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar 
                    dataKey="ventes" 
                    fill={COLORS[3]} 
                    name="Quantité vendue"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-gray-500 text-sm font-medium mb-4">Statut des commandes</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'En attente', value: orderStats.pendingOrders },
                      { name: 'Payées', value: orderStats.paidOrders },
                      { name: 'Annulées', value: orderStats.cancelledOrders }
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {[
                      { name: 'En attente', value: orderStats.pendingOrders },
                      { name: 'Payées', value: orderStats.paidOrders },
                      { name: 'Annulées', value: orderStats.cancelledOrders }
                    ].map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={COLORS[index % COLORS.length]}
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-gray-500 text-sm font-medium">Évolution des ventes par heure</h3>
            <select 
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            >
              {availableDates.map(date => (
                <option key={date.value} value={date.value}>
                  {date.label}
                </option>
              ))}
            </select>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlyEvolutionData} style={CHART_THEME}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_THEME.grid.stroke} />
                <XAxis dataKey="heure" stroke={CHART_THEME.axis.stroke} tick={CHART_THEME.axis.tick} />
                <YAxis stroke={CHART_THEME.axis.stroke} tick={CHART_THEME.axis.tick} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar 
                  dataKey="montant" 
                  fill={COLORS[4]} 
                  name="Chiffre d'affaires"
                  radius={[4, 4, 0, 0]}
                />
                <Bar 
                  dataKey="commandes" 
                  fill={COLORS[5]} 
                  name="Nombre de commandes"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-gray-500 text-sm font-medium mb-4">Détails par classe</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSortClassTable('name')}
                  >
                    Classe {classSortConfig?.key === 'name' ? (classSortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSortClassTable('orders')}
                  >
                    Commandes payées {classSortConfig?.key === 'orders' ? (classSortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSortClassTable('value')}
                  >
                    Montant total {classSortConfig?.key === 'value' ? (classSortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSortClassTable('averageBasket')}
                  >
                    Panier moyen {classSortConfig?.key === 'averageBasket' ? (classSortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {classData.map((item) => (
                  <tr key={item.name}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.orders}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.value.toFixed(2)}€</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.averageBasket.toFixed(2)}€</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-6 mt-8 border border-gray-200">
          <h3 className="text-gray-800 text-xl font-bold mb-6">Articles commandés par statut</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Article</th>
                  <th className="px-6 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Commandés (payés)</th>
                  <th className="px-6 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Commandés (en attente)</th>
                  <th className="px-6 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Total</th>
                  <th className="px-6 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Stock</th>
                  <th className="px-6 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">% Vendu</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-300">
                {/* Articles individuels */}
                <tr className="bg-blue-50">
                  <td colSpan={6} className="px-6 py-3 text-sm font-bold text-blue-800">Articles individuels</td>
                </tr>
                {productStats.articleCounts
                  .filter(article => 
                    !article.name.includes('Pack') && 
                    !article.name.includes('Forfait') &&
                    !article.name.includes('Minimum Syndical')
                  )
                  .map((article) => (
                    <tr key={article.id} className="hover:bg-blue-200 hover:shadow-md transition-all duration-200">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 group-hover:text-blue-900">{article.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">{article.paidCount}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-amber-600">{article.pendingCount}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{article.paidCount + article.pendingCount}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            min="0"
                            value={stockInputValues[article.id] !== undefined ? stockInputValues[article.id] : article.stock}
                            onChange={(e) => handleStockInputChange(article.id, parseInt(e.target.value) || 0)}
                            className="px-2 py-1 w-20 border border-gray-300 rounded text-sm"
                          />
                          <button
                            onClick={() => handleUpdateStock(article.id)}
                            disabled={updatingStock === article.id || stockInputValues[article.id] === article.stock}
                            className={`px-2 py-1 rounded text-xs ${
                              updatingStock === article.id 
                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                                : stockInputValues[article.id] === article.stock
                                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                  : 'bg-blue-500 text-white hover:bg-blue-600'
                            }`}
                          >
                            {updatingStock === article.id ? '...' : 'Maj'}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1">
                          <div 
                            className={`h-2.5 rounded-full ${
                              article.stockPercentage > 90 ? 'bg-red-500' :
                              article.stockPercentage > 75 ? 'bg-orange-500' :
                              article.stockPercentage > 50 ? 'bg-yellow-500' :
                              'bg-green-500'
                            }`}
                            style={{ width: `${article.stockPercentage}%` }}
                          ></div>
                        </div>
                        <span className="text-xs font-medium text-gray-500">{article.stockPercentage.toFixed(1)}%</span>
                      </td>
                    </tr>
                  ))}

                {/* Packs */}
                <tr className="bg-purple-50">
                  <td colSpan={6} className="px-6 py-3 text-sm font-bold text-purple-800">Packs</td>
                </tr>
                {productStats.articleCounts
                  .filter(article => 
                    article.name.includes('Pack') && 
                    !article.name.includes('Forfait') &&
                    !article.name.includes('Minimum Syndical')
                  )
                  .map((article) => (
                    <tr key={article.id} className="hover:bg-purple-200 hover:shadow-md transition-all duration-200">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 group-hover:text-purple-900">{article.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">{article.paidCount}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-amber-600">{article.pendingCount}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{article.paidCount + article.pendingCount}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center space-x-2">
                          <span className={`font-semibold ${article.availablePacks === 0 ? 'text-red-500' : 'text-green-500'}`}>
                            {article.availablePacks} disponibles
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1">
                          <div 
                            className={`h-2.5 rounded-full ${
                              article.stockPercentage > 90 ? 'bg-red-500' :
                              article.stockPercentage > 75 ? 'bg-orange-500' :
                              article.stockPercentage > 50 ? 'bg-yellow-500' :
                              'bg-green-500'
                            }`}
                            style={{ width: `${article.stockPercentage}%` }}
                          ></div>
                        </div>
                        <span className="text-xs font-medium text-gray-500">{article.stockPercentage.toFixed(1)}%</span>
                      </td>
                    </tr>
                  ))}

                {/* Forfaits boissons */}
                <tr className="bg-green-50">
                  <td colSpan={6} className="px-6 py-3 text-sm font-bold text-green-800">Forfaits boissons</td>
                </tr>
                {productStats.articleCounts
                  .filter(article => 
                    article.name.includes('Forfait') ||
                    article.name.includes('Minimum Syndical')
                  )
                  .map((article) => (
                    <tr key={article.id} className="hover:bg-green-200 hover:shadow-md transition-all duration-200">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 group-hover:text-green-900">{article.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">{article.paidCount}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-amber-600">{article.pendingCount}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{article.paidCount + article.pendingCount}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center space-x-2">
                          {article.name.includes('Minimum Syndical') ? (
                            <span className={`font-semibold ${article.availablePacks === 0 ? 'text-red-500' : 'text-green-500'}`}>
                              {article.availablePacks} disponibles
                            </span>
                          ) : (
                            <>
                              <input
                                type="number"
                                min="0"
                                value={stockInputValues[article.id] !== undefined ? stockInputValues[article.id] : article.stock}
                                onChange={(e) => handleStockInputChange(article.id, parseInt(e.target.value) || 0)}
                                className="px-2 py-1 w-20 border border-gray-300 rounded text-sm"
                              />
                              <button
                                onClick={() => handleUpdateStock(article.id)}
                                disabled={updatingStock === article.id || stockInputValues[article.id] === article.stock}
                                className={`px-2 py-1 rounded text-xs ${
                                  updatingStock === article.id 
                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                                    : stockInputValues[article.id] === article.stock
                                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                      : 'bg-blue-500 text-white hover:bg-blue-600'
                                }`}
                              >
                                {updatingStock === article.id ? '...' : 'Maj'}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1">
                          <div 
                            className={`h-2.5 rounded-full ${
                              article.stockPercentage > 90 ? 'bg-red-500' :
                              article.stockPercentage > 75 ? 'bg-orange-500' :
                              article.stockPercentage > 50 ? 'bg-yellow-500' :
                              'bg-green-500'
                            }`}
                            style={{ width: `${article.stockPercentage}%` }}
                          ></div>
                        </div>
                        <span className="text-xs font-medium text-gray-500">{article.stockPercentage.toFixed(1)}%</span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-6 mt-8 border border-gray-200">
          <h3 className="text-gray-800 text-xl font-bold mb-6">Statistiques des tailles de T-shirts</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Taille</th>
                  <th className="px-6 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Commandés (payés)</th>
                  <th className="px-6 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Commandés (en attente)</th>
                  <th className="px-6 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-300">
                {productStats.sizeCounts?.map((size) => (
                  <tr key={size.size} className="hover:bg-gray-100 transition-all duration-200">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">{size.size}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">{size.paidCount}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-amber-600">{size.pendingCount}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{size.paidCount + size.pendingCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {successNotification}
    </div>
  );
}; 